/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import type {ElementNode, LexicalEditor, LexicalNode} from 'lexical';

import {useLexicalComposerContext} from '@lexical/react/LexicalComposerContext';
import useLexicalEditable from '@lexical/react/useLexicalEditable';
import {
  $createTableCellNode,
  $deleteTableColumn__EXPERIMENTAL,
  $deleteTableRow__EXPERIMENTAL,
  $findCellNode,
  $findTableNode,
  $getNodeTriplet,
  $getTableCellNodeFromLexicalNode,
  $getTableColumnIndexFromTableCellNode,
  $getTableNodeFromLexicalNodeOrThrow,
  $getTableRowIndexFromTableCellNode,
  $insertTableColumn__EXPERIMENTAL,
  $insertTableRow__EXPERIMENTAL,
  $isTableCellNode,
  $isTableNode,
  $isTableRowNode,
  $isTableSelection,
  $unmergeCell,
  getTableObserverFromTableElement,
  HTMLTableElementWithWithTableSelectionState,
  TableCellHeaderStates,
  TableCellNode,
  TableRowNode,
  TableSelection,
} from '../../nodes/CustomTableNode/src';
import {
  $createParagraphNode,
  $createTextNode,
  $getNearestNodeFromDOMNode,
  $getRoot,
  $getSelection,
  $isElementNode,
  $isParagraphNode,
  $isRangeSelection,
  $isTextNode,
} from 'lexical';
import * as React from 'react';
import {ReactPortal, useCallback, useEffect, useRef, useState} from 'react';
import {createPortal} from 'react-dom';

import useModal from '../../hooks/useModal';
import ColorPicker from '../../ui/ColorPicker';
import invariant from '../../utils/invariant';
import { generateStableCaptionId } from '../TablePlugin';
import { ColoredNode } from '../../nodes/TableNode';
import { $createTableNodeWithDimensions, $deleteWholeColumnAcrossGroup, $insertWholeColumnAcrossGroup } from '../../nodes/CustomTableNode/src/LexicalTableUtils';
import { $getPageRoot, $isLayoutContainerNode } from '../../nodes/LayoutContainerNode';
import { generateUniqueTableGroupId } from '../../utils/editorstateHelper';
import { findTables } from '../TableCellHeight';

function computeSelectionCount(selection: TableSelection): {
  columns: number;
  rows: number;
} {
  const selectionShape = selection.getShape();
  return {
    columns: selectionShape.toX - selectionShape.fromX + 1,
    rows: selectionShape.toY - selectionShape.fromY + 1,
  };
}

// This is important when merging cells as there is no good way to re-merge weird shapes (a result
// of selecting merged cells and non-merged)
function isTableSelectionRectangular(selection: TableSelection): boolean {
  const nodes = selection.getNodes();
  const currentRows: Array<number> = [];
  let currentRow = null;
  let expectedColumns = null;
  let currentColumns = 0;
  for (let i = 0; i < nodes.length; i++) {
    const node = nodes[i];
    if ($isTableCellNode(node)) {
      const row = node.getParentOrThrow();
      invariant(
        $isTableRowNode(row),
        'Expected CellNode to have a RowNode parent',
      );
      if (currentRow !== row) {
        if (expectedColumns !== null && currentColumns !== expectedColumns) {
          return false;
        }
        if (currentRow !== null) {
          expectedColumns = currentColumns;
        }
        currentRow = row;
        currentColumns = 0;
      }
      const colSpan = node.__colSpan;
      for (let j = 0; j < colSpan; j++) {
        if (currentRows[currentColumns + j] === undefined) {
          currentRows[currentColumns + j] = 0;
        }
        currentRows[currentColumns + j] += node.__rowSpan;
      }
      currentColumns += colSpan;
    }
  }
  return (
    (expectedColumns === null || currentColumns === expectedColumns) &&
    currentRows.every((v) => v === currentRows[0])
  );
}

function $canUnmerge(): boolean {
  const selection = $getSelection();
  if (
    ($isRangeSelection(selection) && !selection.isCollapsed()) ||
    ($isTableSelection(selection) && !selection.anchor.is(selection.focus)) ||
    (!$isRangeSelection(selection) && !$isTableSelection(selection))
  ) {
    return false;
  }
  const [cell] = $getNodeTriplet(selection.anchor);
  return cell.__colSpan > 1 || cell.__rowSpan > 1;
}

function $cellContainsEmptyParagraph(cell: TableCellNode): boolean {
  if (cell.getChildrenSize() !== 1) {
    return false;
  }
  const firstChild = cell.getFirstChildOrThrow();
  if (!$isParagraphNode(firstChild) || !firstChild.isEmpty()) {
    return false;
  }
  return true;
}

function $selectLastDescendant(node: ElementNode): void {
  const lastDescendant = node.getLastDescendant();
  if ($isTextNode(lastDescendant)) {
    lastDescendant.select();
  } else if ($isElementNode(lastDescendant)) {
    lastDescendant.selectEnd();
  } else if (lastDescendant !== null) {
    lastDescendant.selectNext();
  }
}

function currentCellBackgroundColor(editor: LexicalEditor): null | string {
  return editor.getEditorState().read(() => {
    const selection = $getSelection();
    if ($isRangeSelection(selection) || $isTableSelection(selection)) {
      const [cell] = $getNodeTriplet(selection.anchor);
      if ($isTableCellNode(cell)) {
        return cell.getBackgroundColor();
      }
    }
    return null;
  });
}

type TableCellActionMenuProps = Readonly<{
  contextRef: {current: null | HTMLElement};
  onClose: () => void;
  setIsMenuOpen: (isOpen: boolean) => void;
  showColorPickerModal: (
    title: string,
    showModal: (onClose: () => void) => JSX.Element,
  ) => void;
  tableCellNode: TableCellNode;
  cellMerge: boolean;
  enableCaption?: boolean;
  styleConfig?: any;
  showPagination?:any
}>;

function TableActionMenu({
  onClose,
  tableCellNode: _tableCellNode,
  setIsMenuOpen,
  contextRef,
  cellMerge,
  showColorPickerModal,
  enableCaption,
  styleConfig,
  showPagination
}: TableCellActionMenuProps) {
  const [editor] = useLexicalComposerContext();
  const dropDownRef = useRef<HTMLDivElement | null>(null);
  const [tableCellNode, updateTableCellNode] = useState(_tableCellNode);
  const [selectionCounts, updateSelectionCounts] = useState({
    columns: 1,
    rows: 1,
  });
  const [canMergeCells, setCanMergeCells] = useState(false);
  const [canUnmergeCell, setCanUnmergeCell] = useState(false);
  const [backgroundColor, setBackgroundColor] = useState(
    () => currentCellBackgroundColor(editor) || '',
  );
  const CaptionStyle = styleConfig?.heading?.filter((item : any) => item.title === 'Captions')
  const CaptionStyle_ = CaptionStyle && CaptionStyle[0];
  const cellRef = useRef<any>(null)
  useEffect(() => {
    return editor.registerMutationListener(TableCellNode, (nodeMutations) => {
      const nodeUpdated =
        nodeMutations.get(tableCellNode.getKey()) === 'updated';

      if (nodeUpdated) {
        editor.getEditorState().read(() => {
          updateTableCellNode(tableCellNode.getLatest());
        });
        setBackgroundColor(currentCellBackgroundColor(editor) || '');
      }
    });
  }, [editor, tableCellNode]);

  useEffect(() => {
    editor.getEditorState().read(() => {
      const selection = $getSelection();
      // Merge cells
      if ($isTableSelection(selection)) {
        const currentSelectionCounts = computeSelectionCount(selection);
        updateSelectionCounts(computeSelectionCount(selection));
        setCanMergeCells(
          isTableSelectionRectangular(selection) &&
            (currentSelectionCounts.columns > 1 ||
              currentSelectionCounts.rows > 1),
        );
      }
      // Unmerge cell
      setCanUnmergeCell($canUnmerge());
    });
  }, [editor]);

  useEffect(() => {
    const menuButtonElement = contextRef.current;
    const dropDownElement = dropDownRef.current;
    const rootElement = editor.getRootElement();

    if (
      menuButtonElement != null &&
      dropDownElement != null &&
      rootElement != null
    ) {
      const rootEleRect = rootElement.getBoundingClientRect();
      const menuButtonRect = menuButtonElement.getBoundingClientRect();
      dropDownElement.style.opacity = '1';
      const dropDownElementRect = dropDownElement.getBoundingClientRect();
      const margin = 5;
      let leftPosition = menuButtonRect.right + margin;
      if (
        leftPosition + dropDownElementRect.width > window.innerWidth ||
        leftPosition + dropDownElementRect.width > rootEleRect.right
      ) {
        const position =
          menuButtonRect.left - dropDownElementRect.width - margin;
        leftPosition = (position < 0 ? margin : position) + window.pageXOffset;
      }
      dropDownElement.style.left = `${leftPosition + window.pageXOffset}px`;

      let topPosition = menuButtonRect.top;
      if (topPosition + dropDownElementRect.height > window.innerHeight) {
        const position = menuButtonRect.bottom - dropDownElementRect.height;
        topPosition = (position < 0 ? margin : position) + window.pageYOffset;
      }
      dropDownElement.style.top = `${topPosition + +window.pageYOffset}px`;
    }
  }, [contextRef, dropDownRef, editor]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropDownRef.current != null &&
        contextRef.current != null &&
        !dropDownRef.current.contains(event.target as Node) &&
        !contextRef.current.contains(event.target as Node)
      ) {
        setIsMenuOpen(false);
      }
    }

    window.addEventListener('click', handleClickOutside);

    return () => window.removeEventListener('click', handleClickOutside);
  }, [setIsMenuOpen, contextRef]);

  const clearTableSelection = useCallback(() => {
    editor.update(() => {
      if (tableCellNode.isAttached()) {
        const tableNode = $getTableNodeFromLexicalNodeOrThrow(tableCellNode);
        const tableElement = editor.getElementByKey(
          tableNode.getKey(),
        ) as HTMLTableElementWithWithTableSelectionState;

        if (!tableElement) {
          throw new Error('Expected to find tableElement in DOM');
        }

        const tableSelection = getTableObserverFromTableElement(tableElement);
        if (tableSelection !== null) {
          tableSelection.clearHighlight();
        }

        tableNode.markDirty();
        updateTableCellNode(tableCellNode.getLatest());
      }

      const rootNode = $getRoot();
      rootNode.selectStart();
    });
  }, [editor, tableCellNode]);

  const mergeTableCellsAtSelection = () => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isTableSelection(selection)) {
        const {columns, rows} = computeSelectionCount(selection);
        const nodes = selection.getNodes();
        let firstCell: null | TableCellNode = null;
        for (let i = 0; i < nodes.length; i++) {
          const node = nodes[i];
          if ($isTableCellNode(node)) {
            if (firstCell === null) {
              node.setColSpan(columns).setRowSpan(rows);
              firstCell = node;
              const isEmpty = $cellContainsEmptyParagraph(node);
              let firstChild;
              if (
                isEmpty &&
                $isParagraphNode((firstChild = node.getFirstChild()))
              ) {
                firstChild.remove();
              }
            } else if ($isTableCellNode(firstCell)) {
              const isEmpty = $cellContainsEmptyParagraph(node);
              if (!isEmpty) {
                firstCell.append(...node.getChildren());
              }
              node.remove();
            }
          }
        }
        if (firstCell !== null) {
          if (firstCell.getChildrenSize() === 0) {
            firstCell.append($createParagraphNode());
          }
          $selectLastDescendant(firstCell);
        }
        onClose();
      }
    });
  };

  const unmergeTableCellsAtSelection = () => {
    editor.update(() => {
      $unmergeCell();
    });
  };

  const insertTableRowAtSelection = useCallback(
    (shouldInsertAfter: boolean) => {
      editor.update(() => {
        $insertTableRow__EXPERIMENTAL(shouldInsertAfter);
        onClose();
      });
    },
    [editor, onClose],
  );

  const insertTableColumnAtSelection = useCallback(
    (shouldInsertAfter: boolean) => {
      editor.update(() => {
        $insertWholeColumnAcrossGroup(editor, shouldInsertAfter, selectionCounts.columns);
        onClose();
      });
    },
    [editor, onClose, selectionCounts.columns],
  );

  const deleteTableRowAtSelection = useCallback(() => {
    editor.update(() => {
      $deleteTableRow__EXPERIMENTAL();
      onClose();
    });
  }, [editor, onClose]);

  const deleteTableAtSelection = useCallback(() => {
    editor.update(() => {
      const tableNode = $getTableNodeFromLexicalNodeOrThrow(tableCellNode);
      const targetGroupId = tableNode.__groupId;

      if (!targetGroupId) {
        console.warn('No groupId found for selected table node');
        tableNode.setGroupId(generateUniqueTableGroupId())
      }

      const root = showPagination=== true ? $getPageRoot() : $getRoot();
      if(!root) return;
      const nodesToRemove: LexicalNode[] = [];

      // Recursive traversal to collect all table nodes with matching groupId
      const collectMatchingTables = (node: LexicalNode) => {
        if ($isTableNode(node) && node.__groupId === targetGroupId) {
          if(node.__isCaption === true){
            const para = editor.getElementByKey(node.getLatest().__key)
            if(para?.previousElementSibling && para.previousElementSibling !== null){
          const paraNode = $getNearestNodeFromDOMNode(para?.previousElementSibling)
          nodesToRemove.push(paraNode && paraNode !== null ? paraNode : node)
            }
          }
          nodesToRemove.push(node);
        }

        if ($isElementNode(node)) {
          const children = node.getChildren();
          for (const child of children) {
            collectMatchingTables(child);
          }
        }
      };

      collectMatchingTables(root);
      nodesToRemove.forEach((node) => node.remove());
        if (enableCaption) {
          const rootNode = showPagination === true ? $getPageRoot() : $getRoot();
          if(!rootNode) return;
          const tableNodes = ($isLayoutContainerNode(rootNode) ? rootNode.getAllChildren() : rootNode.getChildren()).filter($isTableNode);
          let index1 = 1;
      
          tableNodes.forEach((tableNode) => {
            const previousNode = tableNode.getPreviousSibling();
            const rootNode = showPagination === true ? $getPageRoot() : $getRoot();
            if(!rootNode) return;
            const tables = ($isLayoutContainerNode(rootNode) ? rootNode.getAllChildren() : rootNode.getChildren()).filter($isTableNode);
            const countOfTable = tables.length;
      
            const existingCaptionId = (tableNode as any).__captionId ||
              generateStableCaptionId(`Table ${index1}`, countOfTable);
      
            // Remove any duplicate captions BELOW the table
            const nextNode = tableNode.getNextSibling();
            if (nextNode && $isParagraphNode(nextNode)) {
              nextNode.getChildren().forEach((child) => {
                if (child.getTextContent().startsWith("Table")) {
                  child.remove();
                }
              });
            }
      
            if (previousNode && $isParagraphNode(previousNode)) {
              // Check if previous node contains a caption with ID
              let existingLabelNode: any = null;
              previousNode.getChildren().forEach((child) => {
                if (child.getTextContent().startsWith("Table")) {
                  existingLabelNode = child;
                }
              });
      
              if (existingLabelNode) {
                // If we have an existing label node, update its content but keep the ID
                const existingId = (existingLabelNode as any).getId?.() || existingCaptionId;
                existingLabelNode.remove();
      
                // Create new label with preserved ID
                const newLabelNode = new ColoredNode(
                  `Table ${index1}:`,
                   CaptionStyle_?.fontColor || "black",
                  `${CaptionStyle_?.fontSize}px` || "14.5px",
                  CaptionStyle_?.bold === "Yes" ? "bold" : "normal",
                  CaptionStyle_?.italic === "Yes" ? "italic" : "normal",
                  CaptionStyle_?.alignment || "left",
                  CaptionStyle_?.fontFamily || "Times New Roman",
                  CaptionStyle_?.leadingSpace || "0",
                  CaptionStyle_?.lineSpacing || "1",
                  CaptionStyle_?.trailingSpace || "0",
                  existingId
                );
      
                const firstChild = previousNode.getFirstChild();
                if (firstChild !== null) {
                  firstChild.insertBefore(newLabelNode);
                } else {
                  previousNode.append(newLabelNode);
                }
              } else {
                // No existing label, create a new one
                const newLabelNode = new ColoredNode(
                  `Table ${index1}:`,
                   CaptionStyle_?.fontColor || "black",
                  `${CaptionStyle_?.fontSize}px` || "14.5px",
                  CaptionStyle_?.bold === "Yes" ? "bold" : "normal",
                  CaptionStyle_?.italic === "Yes" ? "italic" : "normal",
                  CaptionStyle_?.alignment || "left",
                  CaptionStyle_?.fontFamily || "Times New Roman",
                  CaptionStyle_?.leadingSpace || "0",
                  CaptionStyle_?.lineSpacing || "1",
                  CaptionStyle_?.trailingSpace || "0",
                  existingCaptionId
                );
      
                const firstChild = previousNode.getFirstChild();
                if (firstChild !== null) {
                  firstChild.insertBefore(newLabelNode);
                } else {
                  previousNode.append(newLabelNode);
                }
              }
            } else {
              // If there's no previous paragraph node, create a new caption
              const tableNumber = `Table ${index1}:`;
              const newCaptionContainer = $createParagraphNode();
              newCaptionContainer.setFormat("left");
      
              const newLabelNode = new ColoredNode(
                tableNumber,
                CaptionStyle_?.fontColor || "black",
                  `${CaptionStyle_?.fontSize}px` || "14.5px",
                  CaptionStyle_?.bold === "Yes" ? "bold" : "normal",
                  CaptionStyle_?.italic === "Yes" ? "italic" : "normal",
                  CaptionStyle_?.alignment || "left",
                  CaptionStyle_?.fontFamily || "Times New Roman",
                  CaptionStyle_?.leadingSpace || "0",
                  CaptionStyle_?.lineSpacing || "1",
                  CaptionStyle_?.trailingSpace || "0",
                existingCaptionId
              );
              newLabelNode.setStyle("font-weight: bold; margin-right: 5px;");
      
              // const captionText = caption.trim() === "" ? "Caption text here" : caption;
              const captionNode = $createTextNode('captionText');
              captionNode.setStyle("color: black; font-style: italic; font-weight: normal;");
      
              newCaptionContainer.append(newLabelNode);
              newCaptionContainer.append($createTextNode(" "));
              newCaptionContainer.append(captionNode);
            }
      
            const hasCaption = (tableNode as any).__isCaption === true;
            if (hasCaption) {
              index1++;
            }
          });
        }
      onClose();
    });
  }, [editor, tableCellNode, clearTableSelection, onClose]);

  const deleteTableColumnAtSelection = useCallback(() => {
    editor.update(() => {
      $deleteWholeColumnAcrossGroup(editor);
      onClose();
    });
  }, [editor, onClose]);

  const toggleTableRowIsHeader = useCallback(() => {
    editor.update(() => {
      const tableNode = $getTableNodeFromLexicalNodeOrThrow(tableCellNode);

      const tableRowIndex = $getTableRowIndexFromTableCellNode(tableCellNode);

      const tableRows = tableNode.getChildren();

      if (tableRowIndex >= tableRows.length || tableRowIndex < 0) {
        throw new Error('Expected table cell to be inside of table row.');
      }

      const tableRow = tableRows[tableRowIndex];

      if (!$isTableRowNode(tableRow)) {
        throw new Error('Expected table row');
      }

      tableRow.getChildren().forEach((tableCell) => {
        if (!$isTableCellNode(tableCell)) {
          throw new Error('Expected table cell');
        }

        tableCell.toggleHeaderStyle(TableCellHeaderStates.ROW);
      });

      clearTableSelection();
      onClose();
    });
  }, [editor, tableCellNode, clearTableSelection, onClose]);

  const toggleTableColumnIsHeader = useCallback(() => {
    editor.update(() => {
      const tableNode = $getTableNodeFromLexicalNodeOrThrow(tableCellNode);

      const tableColumnIndex =
        $getTableColumnIndexFromTableCellNode(tableCellNode);

      const tableRows = tableNode.getChildren<TableRowNode>();
      const maxRowsLength = Math.max(
        ...tableRows.map((row) => row.getChildren().length),
      );

      if (tableColumnIndex >= maxRowsLength || tableColumnIndex < 0) {
        throw new Error('Expected table cell to be inside of table row.');
      }

      for (let r = 0; r < tableRows.length; r++) {
        const tableRow = tableRows[r];

        if (!$isTableRowNode(tableRow)) {
          throw new Error('Expected table row');
        }

        const tableCells = tableRow.getChildren();
        if (tableColumnIndex >= tableCells.length) {
          // if cell is outside of bounds for the current row (for example various merge cell cases) we shouldn't highlight it
          continue;
        }

        const tableCell = tableCells[tableColumnIndex];

        if (!$isTableCellNode(tableCell)) {
          throw new Error('Expected table cell');
        }

        tableCell.toggleHeaderStyle(TableCellHeaderStates.COLUMN);
      }

      clearTableSelection();
      onClose();
    });
  }, [editor, tableCellNode, clearTableSelection, onClose]);

  const handleCellBackgroundColor = useCallback(
    (value: string) => {
      editor.update(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection) || $isTableSelection(selection)) {
          const [cell] = $getNodeTriplet(selection.anchor);
          if ($isTableCellNode(cell)) {
            cell.setBackgroundColor(value);
          }

          if ($isTableSelection(selection)) {
            const nodes = selection.getNodes();

            for (let i = 0; i < nodes.length; i++) {
              const node = nodes[i];
              if ($isTableCellNode(node)) {
                node.setBackgroundColor(value);
              }
            }
          }
        }
      });
    },
    [editor],
  );

  let mergeCellButton: null | JSX.Element = null;
  if (cellMerge) {
    if (canMergeCells) {
      mergeCellButton = (
        <button
          type="button"
          className="item"
          onClick={() => mergeTableCellsAtSelection()}
          data-test-id="table-merge-cells">
          Merge cells
        </button>
      );
    } else if (canUnmergeCell) {
      mergeCellButton = (
        <button
          type="button"
          className="item"
          onClick={() => unmergeTableCellsAtSelection()}
          data-test-id="table-unmerge-cells">
          Unmerge cells
        </button>
      );
    }
  }

    const insertCol = (direction:string)=>{
      editor.update(()=>{
        const selection = $getSelection();
        if($isRangeSelection(selection)){
          const anchor = selection.anchor.getNode();
          const cell = $findCellNode(anchor);
          if(cell){
            cellRef.current = cell.getCellIndex()
            const table = $findTableNode(cell);
            if(table){
                const tableRow :TableRowNode | null = table.getFirstChild();
                if(tableRow){
                  if(direction === 'left'){
                    const rows = table.getChildren();
                    rows.forEach((row:any)=>{
                      const rowIndex = row.getCellAtIndex(cellRef.current);
                      const CellNode = $createTableCellNode(rowIndex.__headerState);
                      rowIndex.insertBefore(CellNode)
                    })
                  }
                  if(direction === 'right'){
                    const rowsR = table.getChildren();
                    rowsR.forEach((rowR:any)=>{
                      const rowIndexR = rowR.getCellAtIndex(cellRef.current);
                      const CellNodeR = $createTableCellNode(rowIndexR.__headerState);
                      rowIndexR.insertAfter(CellNodeR)
                    })
                  }
                }
            }
          }
        }
      })
    }


      const insertRow = (direction:string)=>{
        editor.update(()=>{
          const selection = $getSelection();
          if($isRangeSelection(selection)){
            const anchor = selection.anchor.getNode();
            const cell = $findCellNode(anchor);
            if(cell){
              const table = $findTableNode(cell);
              if(table){
                const tableRow :TableRowNode | null = table.getFirstChild()
                if(tableRow){
                  const cols = tableRow.getChildren().length
                  const newTable = $createTableNodeWithDimensions(1,cols,false);
                  newTable.setGroupId(table.getGroupId())
                  const caption = table.getIsCaption();
                  if(caption !== undefined){
                    newTable.setIsCaption(caption)
                  if(direction === 'above'){
                    table.insertBefore(newTable)
                  }
                  if(direction === 'below'){
                    table.insertAfter(newTable)
                  }
                }else{
                  newTable.setIsCaption(false)
                  if(direction === 'above'){
                    table.insertBefore(newTable)
                  }
                  if(direction === 'below'){
                    table.insertAfter(newTable)
                  }
                }
                if(caption === true && direction === 'above'){
                  table.setIsCaption(false)
                  newTable.setIsCaption(true)
                }
                  }
              }
            }
          }
        })
      }

      const DeleteCol = ()=>{
        editor.update(()=>{
          const selection = $getSelection();
          if(!$isRangeSelection(selection)) return console.warn('selection is valid!');
          const anchor = selection.anchor.getNode();
          const cell = $findCellNode(anchor);
          if(cell){
            cellRef.current = cell.getCellIndex()
            const table = $findTableNode(cell);
            if(table){
                const tableRow = table.getChildren();
                if(tableRow){
                  tableRow.forEach((row:any)=>{
                  const cellAtindex = row.getCellAtIndex(cellRef.current);
                  cellAtindex?.remove()
                  })
                }
            }
          }
        })
      }

  return createPortal(
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      className="dropdown"
      ref={dropDownRef}
      onClick={(e) => {
        e.stopPropagation();
      }}>
      {mergeCellButton}
      <button
        type="button"
        className="item"
        onClick={() =>
          showColorPickerModal('Cell background color', () => (
            <ColorPicker
              color={backgroundColor}
              onChange={handleCellBackgroundColor}
            />
          ))
        }
        data-test-id="table-background-color">
        <span className="text">Background color</span>
      </button>
      <hr />
      <button
        type="button"
        className="item"
        onClick={() => insertTableRowAtSelection(false)}
        data-test-id="table-insert-row-above">
        <span className="text">
          Insert{' '}
          {selectionCounts.rows === 1 ? 'row' : `${selectionCounts.rows} rows`}{' '}
          above
        </span>
      </button>
      <button
        type="button"
        className="item"
        onClick={() => insertTableRowAtSelection(true)}
        data-test-id="table-insert-row-below">
        <span className="text">
          Insert{' '}
          {selectionCounts.rows === 1 ? 'row' : `${selectionCounts.rows} rows`}{' '}
          below
        </span>
      </button>
      <hr />
      <button
        type="button"
        className="item"
        onClick={() => insertCol('left')}
        data-test-id="table-insert-column-before">
        <span className="text">
          Insert{' '}
          {selectionCounts.columns === 1
            ? 'column'
            : `${selectionCounts.columns} columns`}{' '}
          left
        </span>
      </button>
      <button
        type="button"
        className="item"
        onClick={() => insertCol('right')}
        data-test-id="table-insert-column-after">
        <span className="text">
          Insert{' '}
          {selectionCounts.columns === 1
            ? 'column'
            : `${selectionCounts.columns} columns`}{' '}
          right
        </span>
      </button>
      <hr />
      <button
        type="button"
        className="item"
        onClick={() => DeleteCol()}
        data-test-id="table-delete-columns">
        <span className="text">Delete column</span>
      </button>
      <button
        type="button"
        className="item"
        onClick={() => deleteTableRowAtSelection()}
        data-test-id="table-delete-rows">
        <span className="text">Delete row</span>
      </button>
      <button
        type="button"
        className="item"
        onClick={() => deleteTableAtSelection()}
        data-test-id="table-delete">
        <span className="text">Delete table</span>
      </button>
      <hr />
      <button
        type="button"
        className="item"
        onClick={() => toggleTableRowIsHeader()}>
        <span className="text">
          {(tableCellNode.__headerState & TableCellHeaderStates.ROW) ===
          TableCellHeaderStates.ROW
            ? 'Remove'
            : 'Add'}{' '}
          row header
        </span>
      </button>
      <button
        type="button"
        className="item"
        onClick={() => toggleTableColumnIsHeader()}
        data-test-id="table-column-header">
        <span className="text">
          {(tableCellNode.__headerState & TableCellHeaderStates.COLUMN) ===
          TableCellHeaderStates.COLUMN
            ? 'Remove'
            : 'Add'}{' '}
          column header
        </span>
      </button>
    </div>,
    document.body,
  );
}

function TableCellActionMenuContainer({
  anchorElem,
  cellMerge,
  enableCaption,
  styleConfig,
  showPagination
}: {
  anchorElem: HTMLElement;
  cellMerge: boolean;
  enableCaption?: boolean;
  styleConfig?: any;
  showPagination?: any
}): JSX.Element {
  const [editor] = useLexicalComposerContext();

  const menuButtonRef = useRef(null);
  const menuRootRef = useRef(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const [tableCellNode, setTableMenuCellNode] = useState<TableCellNode | null>(
    null,
  );

  const [colorPickerModal, showColorPickerModal] = useModal();

  const moveMenu = useCallback(() => {
    const menu = menuButtonRef.current;
    const selection = $getSelection();
    const nativeSelection = window.getSelection();
    const activeElement = document.activeElement;

    if (selection == null || menu == null) {
      setTableMenuCellNode(null);
      return;
    }

    const rootElement = editor.getRootElement();

    if (
      $isRangeSelection(selection) &&
      rootElement !== null &&
      nativeSelection !== null &&
      rootElement.contains(nativeSelection.anchorNode)
    ) {
      const tableCellNodeFromSelection = $getTableCellNodeFromLexicalNode(
        selection.anchor.getNode(),
      );

      if (tableCellNodeFromSelection == null) {
        setTableMenuCellNode(null);
        return;
      }

      const tableCellParentNodeDOM = editor.getElementByKey(
        tableCellNodeFromSelection.getKey(),
      );

      if (tableCellParentNodeDOM == null) {
        setTableMenuCellNode(null);
        return;
      }

      setTableMenuCellNode(tableCellNodeFromSelection);
    } else if (!activeElement) {
      setTableMenuCellNode(null);
    }
  }, [editor]);

  useEffect(() => {
    return editor.registerUpdateListener(() => {
      editor.getEditorState().read(() => {
        moveMenu();
      });
    });
  });

  useEffect(() => {
    const menuButtonDOM = menuButtonRef.current as HTMLButtonElement | null;

    if (menuButtonDOM != null && tableCellNode != null) {
      const tableCellNodeDOM = editor.getElementByKey(tableCellNode.getKey());

      if (tableCellNodeDOM != null) {
        const tableCellRect = tableCellNodeDOM.getBoundingClientRect();
        const menuRect = menuButtonDOM.getBoundingClientRect();
        const anchorRect = anchorElem.getBoundingClientRect();

        const top = tableCellRect.top - anchorRect.top + 4;
        const left =
          tableCellRect.right - menuRect.width - 10 - anchorRect.left;

        menuButtonDOM.style.opacity = '1';
        menuButtonDOM.style.transform = `translate(${left}px, ${top}px)`;
      } else {
        menuButtonDOM.style.opacity = '0';
        menuButtonDOM.style.transform = 'translate(-10000px, -10000px)';
      }
    }
  }, [menuButtonRef, tableCellNode, editor, anchorElem]);

  const prevTableCellDOM = useRef(tableCellNode);

  useEffect(() => {
    if (prevTableCellDOM.current !== tableCellNode) {
      setIsMenuOpen(false);
    }

    prevTableCellDOM.current = tableCellNode;
  }, [prevTableCellDOM, tableCellNode]);

  return (
    <div className="table-cell-action-button-container" ref={menuButtonRef}>
      {tableCellNode != null && (
        <>
          <button
            type="button"
            className="table-cell-action-button chevron-down"
            onClick={(e) => {
              e.stopPropagation();
              setIsMenuOpen(!isMenuOpen);
            }}
            ref={menuRootRef}>
            <i className="chevron-down" />
          </button>
          {colorPickerModal}
          {isMenuOpen && (
            <TableActionMenu
              contextRef={menuRootRef}
              setIsMenuOpen={setIsMenuOpen}
              onClose={() => setIsMenuOpen(false)}
              tableCellNode={tableCellNode}
              cellMerge={cellMerge}
              showColorPickerModal={showColorPickerModal}
              enableCaption={enableCaption}
              styleConfig={styleConfig}
              showPagination={showPagination}
            />
          )}
        </>
      )}
    </div>
  );
}

export default function TableActionMenuPlugin({
  anchorElem = document.body,
  cellMerge = false,
  enableCaption,
  styleConfig,
  showPagination
}: {
  anchorElem?: HTMLElement;
  cellMerge?: boolean;
  enableCaption?: boolean;
  styleConfig?: any; 
  showPagination?: any;
}): null | ReactPortal {
  const isEditable = useLexicalEditable();
  return createPortal(
    isEditable ? (
      <TableCellActionMenuContainer
        anchorElem={anchorElem}
        cellMerge={cellMerge}
        enableCaption={enableCaption}
        styleConfig={styleConfig}
        showPagination={showPagination}
      />
    ) : null,
    anchorElem,
  );
}
